import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import Card from '../ui/Card'
import Button from '../ui/Button'
import PageLoader from '../ui/PageLoader'
import PageHeader from '../layout/PageHeader'
import ProjectPageTitle from '../layout/ProjectPageTitle'
import { formatPipeSetDimensions, joinMetaParts } from '../../utils/projectSubtitle'

import { useProjects } from '../../contexts/ProjectsContext'
import { useOptionalWpsSections } from '../../contexts/WpsSectionsContext'
import { fetchSectionsWithVersion } from '../../utils/sectionsClient'
import { fetchTeamMembers } from '../../api/teamMembersApi'
import { generatePqrReport, listReports } from '../../utils/reportsClient'
import type { PqrEditableSnapshot } from '../../utils/pqrPreviewDiff'
import { readMaterialsFromBaseSection } from '../../utils/pqrMaterialsMapper'
import { readJointPreparationFromBevelSection } from '../../utils/pqrJointPreparationMapper'
import { readWeldingVariablesFromSections } from '../../utils/pqrWeldingVariablesMapper'
import { readWeldingParametersFromSections } from '../../utils/pqrWeldingParametersMapper'
import { readNotesFromSections } from '../../utils/pqrNotesMapper'
import type { TeamMember } from '../../types/teamMember'

import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import {
  usePqrPreviewStaging,
  type PqrSectionName
} from './pqrPreview/pqrPreviewStaging'
import PqrPreviewCanvas, {
  type ProjectIdentitySummary
} from './pqrPreview/PqrPreviewCanvas'
import FloorSheetPreviewCanvas from './pqrPreview/FloorSheetPreviewCanvas'
import type { SignatoryDraft } from '../../types/signatory'
import type { FloorSheetPreviewData } from '../../utils/pqrPreviewClient'
import { usePqrPreviewData } from './pqrPreview/usePqrPreviewData'
import type { PqrPreviewTemplateData } from '../../utils/pqrPreviewClient'
import PqrPreviewToolbar from './pqrPreview/PqrPreviewToolbar'
// pqr-report-history-and-rev slice 05: the "Change Weld" affordance was
// removed from the preview toolbar, so this page no longer needs the
// picker. Weld scope is fixed at picker time on PqrDetails and surfaced
// here as a read-only pill via PqrPreviewHeaderPills.
import IdentifiersModal from './pqrPreview/IdentifiersModal'
import SignaturesModal from './pqrPreview/SignaturesModal'
import PqrPreviewHeaderPills from './pqrPreview/PqrPreviewHeaderPills'
import ConfirmDialog, { type ConfirmDialogTone } from '../ui/ConfirmDialog'
import PdfViewer from '../ui/PdfViewer'
// JointPreparationModal removed — section is read-only as of round 3 of
// pqr-five-changes. The bevel image + dimensions are admin-authored and
// the active bevel is implicit from the picked weld.
// WeldingVariablesModal removed — Slice 07 of pqr-preview-field-navigation
// makes each cell of the Welding Variables matrix navigate to the
// per-pass WpsWeldDetails page, where edits happen in place.
// WeldingParametersModal removed — Slice 08 of pqr-preview-field-navigation
// makes each cell of the Welding Parameters matrix navigate to the
// per-weld WeldParameters page so edits happen alongside the source
// telemetry.
import PqrDiffConfirmDialog, {
  type PqrConfirmMode
} from './pqrPreview/PqrDiffConfirmDialog'
import { exportPqrPreviewExcel } from '../../utils/pqr/exportPqrExcel'
import { readToleranceFromWeldSection } from '../../utils/pqr/weldParamsWpsLimits'

/**
 * Per-tab sessionStorage prefix for the *staged* (not-yet-saved) PQR
 * snapshot. Persistence is intentionally session-scoped + keyed by the
 * active PQR identity (project + pipe set + weld) so that:
 *
 *   - Navigating away to a section editor (e.g. WpsBevelDetails) and
 *     returning via PqrReturnBanner restores the user's in-flight
 *     signatures / identifiers — fixing the "Save & Generate stays
 *     disabled because preparedBy is blank after the round-trip" bug.
 *   - Reloading the tab survives the staged work.
 *   - A different PQR (other weld, other pipe set, other project) gets
 *     its own slot — no cross-PQR bleeding.
 *   - Closing the tab drops the staged state; the next session starts
 *     from the server-saved baseline. Replaces the old
 *     `wps-studio:pqr-preview-defaults` localStorage that was used to
 *     auto-prefill Prepared/Reviewed By across sessions (per spec, each
 *     save now requires the user to (re)enter signatories).
 */
const STAGED_SESSION_PREFIX = 'wps-studio:pqr-preview-staged'

function buildStagedSessionKey(
  projectId: string | undefined,
  pipeSetId: string | undefined,
  weldIds: string[]
): string | null {
  if (!projectId || !pipeSetId) return null
  const scopeKey =
    weldIds.length > 0 ? [...weldIds].sort().join('|') : 'default'
  return `${STAGED_SESSION_PREFIX}:${projectId}:${pipeSetId}:${scopeKey}`
}

function readStagedSession(key: string): PqrEditableSnapshot | undefined {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return undefined
    return JSON.parse(raw) as PqrEditableSnapshot
  } catch {
    return undefined
  }
}

function writeStagedSession(key: string, snapshot: PqrEditableSnapshot): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(snapshot))
  } catch {
    /* quota / private mode — non-fatal */
  }
}

function clearStagedSession(key: string): void {
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    /* non-fatal */
  }
}

function readPqrSection(sections: Record<string, unknown>): Partial<PqrEditableSnapshot> {
  const raw = sections.pqr
  if (!raw || typeof raw !== 'object') return {}
  const obj = raw as Record<string, unknown>
  const person = (v: unknown): PqrEditableSnapshot['preparedBy'] | undefined => {
    if (!v || typeof v !== 'object') return undefined
    const p = v as Record<string, unknown>
    const name = typeof p.name === 'string' ? p.name : ''
    if (!name.trim()) return undefined
    const out: PqrEditableSnapshot['preparedBy'] = {
      id: typeof p.id === 'string' ? p.id : '',
      name
    }
    if (typeof p.title === 'string' && p.title.trim()) out.title = p.title.trim()
    if (typeof p.date === 'string' && p.date.trim()) out.date = p.date.trim()
    if (typeof p.signature === 'string' && p.signature.trim()) out.signature = p.signature.trim()
    if (typeof p.stampImage === 'string' && p.stampImage.trim()) {
      out.stampImage = p.stampImage.trim()
    }
    if (typeof p.signedAt === 'string' && p.signedAt.trim()) out.signedAt = p.signedAt.trim()
    if (typeof p.memberId === 'string' && p.memberId.trim()) out.memberId = p.memberId.trim()
    return out
  }
  return {
    pqrNumber: typeof obj.pqrNumber === 'string' ? obj.pqrNumber : '',
    revision: typeof obj.revision === 'string' ? obj.revision : '',
    preparedBy: person(obj.preparedBy),
    reviewedBy: person(obj.reviewedBy),
    comments: typeof obj.comments === 'string' ? obj.comments : ''
  }
}

const EMPTY_SNAPSHOT: PqrEditableSnapshot = {
  pqrNumber: '',
  revision: '',
  preparedBy: { id: '', name: '' },
  reviewedBy: { id: '', name: '' },
  comments: '',
  materials: [],
  jointPreparation: undefined,
  weldingVariables: undefined,
  weldingParameters: undefined,
  notes: undefined
}

function buildInitialSnapshot(
  fromSections: Record<string, unknown>,
  weldId: string | undefined
): PqrEditableSnapshot {
  const persisted = readPqrSection(fromSections)
  return {
    pqrNumber: persisted.pqrNumber ?? '',
    revision: persisted.revision ?? '',
    // No cross-session prefill: signatories must be (re)entered for every
    // save. Sessions are bridged by sessionStorage instead (see
    // STAGED_SESSION_PREFIX above), which only restores work the user
    // already staged in the *current* tab session.
    preparedBy: persisted.preparedBy ?? { id: '', name: '' },
    reviewedBy: persisted.reviewedBy ?? { id: '', name: '' },
    comments: persisted.comments ?? '',
    // SCOPED-PQR: when a weld is picked, the Materials editor mirrors the
    // canvas — two rows (Pipe 1, Pipe 2) sourced from the weld's pup heats
    // — so the modal can't list more pipes than the preview shows.
    materials: readMaterialsFromBaseSection(fromSections, weldId),
    jointPreparation: readJointPreparationFromBevelSection(fromSections),
    weldingVariables: readWeldingVariablesFromSections(fromSections),
    weldingParameters: readWeldingParametersFromSections(fromSections),
    notes: readNotesFromSections(fromSections)
  }
}

/**
 * Compute the next sequential PQR identifier ("PQR-001", "PQR-002", …)
 * given the count of existing PQR reports in the pipe set's report list.
 *
 * Used by the "Generate PQR Report" entry-point (which appends `?new=1`
 * to the route) so users land in a pre-populated draft instead of an
 * empty form. Format: three-digit zero-padded suffix.
 */
function computeNextPqrNumber(existingPqrCount: number): string {
  return `PQR-${String(existingPqrCount + 1).padStart(3, '0')}`
}

/**
 * Overlay cheap-to-merge staged edits (identifiers, signatures) onto the
 * server-aggregated preview snapshot. Heavier section edits round-trip
 * through `Save -> refetch` so the canvas always reflects the freshly
 * aggregated server payload after a save.
 *
 * Note: `comments` is intentionally NOT overlaid. As of Slice 03 of
 * pqr-preview-field-navigation, comments are sourced from the picked
 * weld's FloorSheet `operators.notes` server-side; the canvas mirrors
 * the server value verbatim and the user edits it by navigating to
 * FloorSheet.
 */
function mergeSignatoryForPreview(
  staged: PqrEditableSnapshot['preparedBy'],
  preview: PqrPreviewTemplateData['signatures']['preparedBy']
): PqrPreviewTemplateData['signatures']['preparedBy'] {
  return {
    name: staged.name?.trim() || preview.name,
    title: staged.title?.trim() || preview.title,
    date: staged.date?.trim() || preview.date,
    signatureImage: staged.signature?.trim() || preview.signatureImage,
    stampImage: staged.stampImage?.trim() || preview.stampImage
  }
}

function recordedByDraftFromPreview(
  recordedBy: FloorSheetPreviewData['recordedBy'] | undefined
): SignatoryDraft {
  return {
    id: '',
    name: recordedBy?.name ?? '',
    title: recordedBy?.title,
    date: recordedBy?.date,
    signature: recordedBy?.signatureImage,
    stampImage: recordedBy?.stampImage
  }
}

function recordedByListFromPreview(sheet: FloorSheetPreviewData): SignatoryDraft[] {
  const list =
    Array.isArray(sheet.recordedByList) && sheet.recordedByList.length > 0
      ? sheet.recordedByList
      : [sheet.recordedBy ?? { name: '' }]
  const drafts = list.map(recordedByDraftFromPreview)
  return drafts.length > 0 ? drafts : [{ id: '', name: '' }]
}

function mergeSignatoriesIntoSheet(
  sheet: FloorSheetPreviewData,
  recordedDrafts?: SignatoryDraft[],
  reviewedDraft?: SignatoryDraft
): FloorSheetPreviewData {
  let next = sheet
  if (recordedDrafts && recordedDrafts.length > 0) {
    const named = recordedDrafts.filter((d) => d.name.trim())
    const list = (named.length > 0 ? named : recordedDrafts).map((draft) => ({
      name: draft.name,
      title: draft.title,
      date: draft.date,
      signatureImage: draft.signature,
      stampImage: draft.stampImage
    }))
    next = {
      ...next,
      recordedBy: list[0] ?? { name: '' },
      recordedByList: list
    }
  }
  if (reviewedDraft) {
    next = {
      ...next,
      reviewedBy: {
        name: reviewedDraft.name,
        title: reviewedDraft.title,
        date: reviewedDraft.date,
        signatureImage: reviewedDraft.signature,
        stampImage: reviewedDraft.stampImage
      }
    }
  }
  return next
}

function signatoryDraftToGeneratePerson(draft: SignatoryDraft) {
  return {
    id: draft.id,
    name: draft.name,
    memberId: draft.memberId,
    title: draft.title,
    date: draft.date,
    signature: draft.signature,
    stampImage: draft.stampImage,
    signedAt: draft.signedAt
  }
}

function composePreviewSnapshot(
  preview: PqrPreviewTemplateData,
  staged: PqrEditableSnapshot
): PqrPreviewTemplateData {
  const pickStaged = (stagedValue: string, fallback: string): string =>
    stagedValue && stagedValue.trim().length > 0 ? stagedValue : fallback

  return {
    ...preview,
    identifiers: {
      ...preview.identifiers,
      pqrNumber: pickStaged(staged.pqrNumber, preview.identifiers.pqrNumber),
      revision: pickStaged(staged.revision, preview.identifiers.revision)
    },
    signatures: {
      preparedBy: mergeSignatoryForPreview(staged.preparedBy, preview.signatures.preparedBy),
      reviewedBy: mergeSignatoryForPreview(staged.reviewedBy, preview.signatures.reviewedBy)
    }
  }
}

/**
 * PQR Preview — the editable, PDF-faithful page introduced in
 * `.scratch/pqr-modal-preview` Slice 01.
 *
 * Responsibilities (kept thin; almost everything is delegated):
 *   - Load project + sections + team members on mount.
 *   - Maintain server/staged snapshots via `usePqrPreviewStaging`.
 *   - Render the toolbar (Discard / Save / Save & Generate Report), the
 *     PDF-faithful canvas, the diff-confirm dialog and the Identifiers
 *     modal (the single modal wired in Slice 01).
 *   - Persist via `POST /reports/generate-pqr` with `generatePdf` set per
 *     toolbar action. Surface 409 stale-version conflicts with a reload
 *     prompt. Guard against tab-close / route-change while staged-dirty.
 *
 * Later slices (02-08) replace each remaining read-only canvas region with
 * its own modal-driven editor; the orchestration here does not change.
 */
export default function PqrPreviewPage() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { getProject } = useProjects()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((p) => p.id === pipeId)
  // PqrPreviewPage lives under <PipeSetLayout> which mounts a
  // <WpsSectionsProvider>. That provider holds an in-memory copy of
  // `sections` shared with every WPS editor (WpsBevelDetails, etc.) under
  // the same pipe-set route, and its `updateSection(key, data)` PUTs the
  // ENTIRE `sections` blob — so any editor save after a PQR save would
  // otherwise overwrite the just-persisted `sections.pqr` (incl. the
  // freshly-bumped `revision`) with the stale cached copy. We refresh
  // the provider's cache after every successful PQR save so the next
  // editor PUT carries the latest `sections.pqr` and the user-visible
  // Rev. survives the row-click → editor → return round-trip.
  //
  // Uses the *optional* hook so existing test harnesses (which render
  // PqrPreviewPage without the surrounding PipeSetLayout) keep working.
  // Production always has the provider; tests get `null` and the
  // refresh becomes a no-op.
  const wpsSections = useOptionalWpsSections()
  // `?new=1` (set by the "Generate PQR Report" button on PqrDetails) tells
  // the page to start a brand-new draft: clear the loaded PQR No. / Rev. /
  // signatories and auto-fill the next sequential PQR No.
  const newDraftRequested = searchParams.get('new') === '1'
  // `?weldId=<id>` (set by the entry-point picker on PqrDetails, also
  // mutated by the "Change Weld" button on the toolbar) scopes the
  // preview + the generated PDF to a single weld. Missing/empty = legacy
  // aggregated view. We normalise to `undefined` so the hook + the save
  // body can treat "no scope" as a single sentinel value.
  const weldIdParam = searchParams.get('weldId')
  const weldIdsParam = searchParams.get('weldIds')
  const activeWeldIds = useMemo(
    () =>
      weldIdsParam && weldIdsParam.trim() !== ''
        ? weldIdsParam
            .split(',')
            .map((w) => w.trim())
            .filter((w) => w.length > 0)
        : [],
    [weldIdsParam]
  )
  const activeWeldId =
    activeWeldIds[0] ??
    (weldIdParam && weldIdParam.trim() !== '' ? weldIdParam.trim() : undefined)
  // Re-entrancy guard for the Save flow. Without this a rapid double-click
  // on "Save" or "Save & Generate" could fire two near-simultaneous POSTs,
  // and the second would 409 because the first already bumped the
  // sections.version. `busy` alone is insufficient because state updates
  // are batched / async; a ref flips synchronously inside the click.
  const saveInFlightRef = useRef(false)

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [members, setMembers] = useState<TeamMember[]>([])
  const [staleBanner, setStaleBanner] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [identifiersOpen, setIdentifiersOpen] = useState(false)
  const [signaturesOpen, setSignaturesOpen] = useState(false)
  const [recordedByOverrides, setRecordedByOverrides] = useState<
    Record<string, SignatoryDraft[]>
  >({})
  const [reviewedByOverrides, setReviewedByOverrides] = useState<
    Record<string, SignatoryDraft>
  >({})
  const [recordedByEditWeldId, setRecordedByEditWeldId] = useState<string | null>(null)
  /** Preview pager removed — PQR and Floor Sheet render stacked vertically. */
  // Joint Preparation editing was removed in round 3 of pqr-five-changes —
  // the section is now read-only on the canvas. The image + dimensions
  // come from the admin-uploaded bevel geometry row and the active bevel
  // index of the picked weld, neither of which the PQR author owns.
  const [confirmMode, setConfirmMode] = useState<PqrConfirmMode | null>(null)
  const [confirmEmailOnly, setConfirmEmailOnly] = useState(false)
  // pqr-report-history-and-rev slice 05: the per-weld lookup map for
  // the header pill. Built from `floorsheet.welds[*]` during `load()`
  // alongside the synthetic-id map below. `null` weldNumber means
  // "weld present but operator hasn't assigned a number".
  const [weldNumberByWeldId, setWeldNumberByWeldId] = useState<
    Record<string, string>
  >({})
  // Polish-pass slice 06: in-app ConfirmDialog replaces window.confirm
  // for the discard / reload-baseline flows. Mounting one dialog and
  // driving it with a small spec object keeps the three call-sites
  // (toolbar Discard, stale-banner Reload, unsaved-banner Discard)
  // visually identical instead of each rendering its own modal.
  const [pageConfirm, setPageConfirm] = useState<{
    title: string
    description: string
    confirmLabel: string
    tone?: ConfirmDialogTone
    onConfirm: () => void
  } | null>(null)
  // Polish-pass slice 06: Save & Generate Report now opens the freshly
  // generated PDF in the in-app PdfViewer overlay (same surface used by
  // PqrDetails > Download) instead of a new browser tab — the user
  // reviews then chooses Download or Open-in-New-Tab from the viewer
  // header. `null` keeps the overlay closed.
  const [viewer, setViewer] = useState<{ fileUrl: string; fileName: string } | null>(
    null
  )
  // Resolution map: user-typed `weldId` -> synthetic `floorsheet.welds[i].id`.
  // Needed because the `/floorsheet/:weldId/parameters` route's `:weldId`
  // param is misnamed and actually expects the synthetic id, while the
  // PQR Preview's `?weldId=` query carries the user-typed id (the same
  // value the server uses for `resolvePqrWeldScope`). PqrFieldLinkContext
  // surfaces this lookup so the WeldParameters URL builder can swap
  // identifiers without leaking the discrepancy into other navigations.
  const [weldIdToSyntheticId, setWeldIdToSyntheticId] = useState<
    Record<string, string>
  >({})

  const staging = usePqrPreviewStaging({
    initialServer: EMPTY_SNAPSHOT,
    initialVersion: 0,
    initialHasPersistedPqr: false
  })

  // Preview data is the same `PqrTemplateData` payload that drives the PDF.
  // The canvas renders this 1:1 (single source of truth) and we re-fetch
  // it after every successful save so aggregation (notes / equipment /
  // accompanying reports / comments) stays current. `activeWeldId` scopes
  // the aggregation server-side; changing it via the "Change Weld" button
  // (which mutates the URL) refetches automatically through the hook's
  // dep list.
  const preview = usePqrPreviewData(
    id,
    pipeId,
    activeWeldIds.length > 0 ? activeWeldIds : activeWeldId
  )

  // ----- load -----------------------------------------------------------
  const load = useCallback(async () => {
    if (!id || !pipeId) return
    setLoading(true)
    setLoadError(null)
    try {
      const [sec, team] = await Promise.all([
        fetchSectionsWithVersion(id, pipeId),
        fetchTeamMembers(id, { status: 'active', page: 1, pageSize: 200 })
      ])
      const initial = buildInitialSnapshot(sec.sections, activeWeldId)
      // Precedence for the staged-snapshot override:
      //   1. ?new=1 picker handoff — URL / count-based seed; clears stale
      //      sessionStorage for this weld scope first.
      //   2. sessionStorage — same-tab restoration after section editor
      //      round-trips (only when not starting a new draft).
      //   3. (none) — staged starts equal to server.
      //
      // If the user clicked "Generate PQR Report" (which appends `?new=1`),
      // build the picker-seeded staged snapshot and feed it into
      // `staging.reset` as the optional `stagedOverride` so server +
      // staged advance atomically in a SINGLE state update.
      //
      // pqr-report-history-and-rev slice 03: when `pqrNumber` AND
      // `revision` URL params are present (picker-supplied values via
      // the four-param handoff) use them verbatim. When they're absent
      // (back-compat for any caller still on the two-param shape), fall
      // back to the historical count-based derivation.
      //
      // Why an atomic reset instead of reset-then-apply: React 18
      // `<StrictMode>` (enabled in `client/src/index.tsx`) double-invokes
      // mount effects in dev. The previous reset-then-apply pattern
      // gated the apply behind a useRef "fire once" guard, but the
      // second load()'s unconditional `reset(initial)` still overwrote
      // staged — silently dropping the picker's payload. Folding the
      // seed into reset makes it idempotent: both StrictMode runs call
      // `reset(initial, …, seededStaged)` with identical args and
      // converge on the same final state regardless of resolution
      // order. The persisted server baseline is preserved so the
      // unsaved-banner + leave-guard correctly treat the picker
      // handoff as a staged edit awaiting Save.
      let seededStaged: PqrEditableSnapshot | undefined
      const sessionKey = buildStagedSessionKey(
        id,
        pipeId,
        activeWeldIds.length > 0 ? activeWeldIds : activeWeldId ? [activeWeldId] : []
      )
      // Precedence:
      //   1. ?new=1 picker / history handoff — URL (or count-based) wins and
      //      clears any abandoned session draft for this weld scope so a
      //      stale Rev cannot override a fresh first PQR.
      //   2. sessionStorage — resume in-progress edits after section nav.
      //   3. (none) — staged starts equal to server baseline.
      if (newDraftRequested) {
        if (sessionKey) clearStagedSession(sessionKey)
        const urlPqrNumber = searchParams.get('pqrNumber')
        const urlRevision = searchParams.get('revision')
        // New weld-scoped drafts must not inherit pipe-set-global
        // sections.pqr identifiers as the visible starting point.
        const blankIdentifiers = { ...initial, pqrNumber: '', revision: '0' }
        if (urlPqrNumber && urlRevision) {
          seededStaged = {
            ...blankIdentifiers,
            pqrNumber: urlPqrNumber,
            revision: urlRevision
          }
        } else {
          try {
            const reportsList = await listReports({ projectId: id, pipeSetId: pipeId })
            const existingPqrCount = (reportsList.items || []).filter(
              (item) =>
                item.tag === 'PQR' &&
                !(typeof item.deletedAt === 'string' && item.deletedAt.trim().length > 0)
            ).length
            seededStaged = {
              ...blankIdentifiers,
              pqrNumber: computeNextPqrNumber(existingPqrCount),
              revision: '0'
            }
          } catch (err) {
            // Non-fatal: if the report-count fetch fails the user still gets
            // blank identifiers and can type the PQR No. manually.
            console.warn('Failed to derive next PQR number', err)
            seededStaged = blankIdentifiers
          }
        }
      } else {
        const restoredStaged = sessionKey ? readStagedSession(sessionKey) : undefined
        if (restoredStaged) {
          // Merge: take the user's in-flight identifiers / signatures /
          // comments over the sessionStorage snapshot, but keep the
          // freshly-aggregated server-derived sections (materials, joint
          // prep, welding variables/parameters, notes) so re-opening the
          // preview after editing a downstream section reflects the new
          // upstream data instead of a stale aggregate cached at the
          // time the user opened SignaturesModal.
          seededStaged = {
            ...initial,
            pqrNumber: restoredStaged.pqrNumber ?? initial.pqrNumber,
            revision: restoredStaged.revision ?? initial.revision,
            preparedBy: restoredStaged.preparedBy ?? initial.preparedBy,
            reviewedBy: restoredStaged.reviewedBy ?? initial.reviewedBy,
            comments: restoredStaged.comments ?? initial.comments
          }
        }
      }
      staging.reset(initial, sec.version, Boolean(sec.sections.pqr), seededStaged)
      setMembers(team.items.filter((m) => m.status === 'active'))
      // Build the user-typed-weldId -> synthetic-id resolution map AND
      // the user-typed-weldId -> weldNumber map for the header pill in
      // a single pass over floorsheet.welds. (Slice 05 dropped the
      // `weldOptions` cache that previously fed the on-page picker.)
      const fs = (sec.sections as { floorsheet?: { welds?: unknown } }).floorsheet
      const weldRows = Array.isArray(fs?.welds) ? (fs!.welds as unknown[]) : []
      const idMap: Record<string, string> = {}
      const numberMap: Record<string, string> = {}
      for (const raw of weldRows) {
        if (!raw || typeof raw !== 'object') continue
        const w = raw as { id?: unknown; weldId?: unknown; weldNumber?: unknown }
        const userTyped = typeof w.weldId === 'string' ? w.weldId.trim() : ''
        const synthetic = typeof w.id === 'string' ? w.id.trim() : ''
        if (userTyped && synthetic) idMap[userTyped] = synthetic
        if (userTyped) {
          numberMap[userTyped] =
            typeof w.weldNumber === 'string' ? w.weldNumber.trim() : ''
        }
      }
      setWeldIdToSyntheticId(idMap)
      setWeldNumberByWeldId(numberMap)

      // Strip the seed-only params (`new`, `pqrNumber`, `revision`) so a
      // refresh doesn't keep re-applying them on top of saved values,
      // while preserving `weldId` (Phase 2 scoping) so the per-weld
      // preview survives a page refresh. Idempotent: only re-write the
      // URL when the stripped version actually differs (the second
      // StrictMode mount-effect run sees an already-stripped URL).
      if (newDraftRequested) {
        const next = new URLSearchParams(searchParams)
        next.delete('new')
        next.delete('pqrNumber')
        next.delete('revision')
        if (next.toString() !== searchParams.toString()) {
          setSearchParams(next, { replace: true })
        }
      }
    } catch (err: any) {
      console.error('Failed to load PQR preview', err)
      setLoadError(err?.message || 'Failed to load PQR preview')
    } finally {
      setLoading(false)
    }
  }, [
    id,
    pipeId,
    staging,
    newDraftRequested,
    setSearchParams,
    activeWeldId,
    activeWeldIds,
    searchParams
  ])

  useEffect(() => {
    void load()
    // We still re-load when weld scope query params change because the
    // URL is the single source of truth. In slice 05 the in-page
    // "Change Weld" affordance was removed, so scope only changes when
    // the user lands from `PqrDetails` with new `?weldId=` / `?weldIds=`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, pipeId, activeWeldId, weldIdsParam])

  // ----- leave guard ----------------------------------------------------
  // Guard callback is wired after floor-sheet signatory dirty helpers below.

  // Sync the staged snapshot to sessionStorage so a round-trip through a
  // section editor (or a tab reload) restores the user's in-flight
  // signatures / identifiers — fixes the "Save & Generate stays disabled
  // because Prepared/Reviewed By is blank after coming back" bug.
  //
  //   - Skipped while `loading` is true so the no-op initial render
  //     (before `load()` rehydrates) never clears a key the load() is
  //     about to consume.
  //   - On every staged change: write when dirty, clear when clean. This
  //     means a successful Save / Save & Generate clears the cache
  //     automatically (markServerSaved makes staged === server), and a
  //     Discard does too.
  useEffect(() => {
    if (loading) return
    const key = buildStagedSessionKey(
      id,
      pipeId,
      activeWeldIds.length > 0 ? activeWeldIds : activeWeldId ? [activeWeldId] : []
    )
    if (!key) return
    if (staging.isDirty()) {
      writeStagedSession(key, staging.stagedSnapshot)
    } else {
      clearStagedSession(key)
    }
    // `staging.isDirty` is a referentially-changing useCallback whose
    // outputs are fully determined by `staging.stagedSnapshot` and
    // `staging.serverSnapshot`; depending on the snapshots is sufficient
    // and avoids the effect re-running on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, id, pipeId, activeWeldId, staging.stagedSnapshot, staging.serverSnapshot])

  // ----- canvas section click -------------------------------------------
  // Project-level identity (company / contractor / industry spec / etc.)
  // now flows through the preview payload itself, so the canvas no longer
  // needs the rich `ProjectIdentitySummary` object — only the project name
  // (used in the page footer) is required.
  const projectSummary: ProjectIdentitySummary = useMemo(
    () => ({ projectName: project?.name ?? '' }),
    [project]
  )

  /**
   * Each slice converts another read-only section into a modal-driven
   * editor. Sections still in fallback render a small "Edit in WPS form"
   * link pointing at the relevant WPS editor.
   *
   *   Slice 01 — Identifiers      → modal
   *   Slice 02 — Signatures       → modal
   *   Slice 03 — Comments         → modal
   *   Slice 04 — Materials        → modal
   *   Slice 05 — Joint Preparation→ modal
   *   Slice 06 — Welding Variables→ modal
   *   Slice 07 — Welding Params   → modal
   *   Slice 08 — Notes            → modal (final slice — every section
   *                                  is now modal-driven)
   */
  const wpsFormRoot = useMemo(
    () => (project && pipeSet ? `/projects/${project.id}/next/${pipeSet.id}` : null),
    [project, pipeSet]
  )

  const handleExportExcel = useCallback(async () => {
    if (!id || !pipeId) {
      toast.error('Project context is missing')
      return
    }
    const scopedWeldIds =
      activeWeldIds.length > 0 ? activeWeldIds : activeWeldId ? [activeWeldId] : []
    if (scopedWeldIds.length === 0) {
      toast.error('Select at least one weld to export')
      return
    }
    const syntheticId = activeWeldId ? weldIdToSyntheticId[activeWeldId] : undefined
    const weldSection =
      (activeWeldId && wpsSections?.getSection(`weldparams.${activeWeldId}`)) ||
      (syntheticId && wpsSections?.getSection(`weldparams.${syntheticId}`))
    const boundary = readToleranceFromWeldSection(weldSection)
    try {
      await exportPqrPreviewExcel({
        projectId: id,
        pipeSetId: pipeId,
        weldIds: scopedWeldIds.length > 1 ? scopedWeldIds : undefined,
        weldId: scopedWeldIds.length === 1 ? scopedWeldIds[0] : undefined,
        boundary
      })
      toast.success('Excel export started')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Excel export failed')
    }
  }, [
    id,
    pipeId,
    activeWeldId,
    activeWeldIds,
    wpsSections,
    weldIdToSyntheticId
  ])

  const teamMembersHref = useMemo(
    () => (project ? `/projects/${project.id}/team-members` : '/team-members'),
    [project]
  )

  const handleSectionClick = useCallback((section: PqrSectionName) => {
    if (section === 'identifiers') {
      setIdentifiersOpen(true)
      return
    }
    // Prepared/Reviewed By were removed from the PQR canvas; Floor Sheet
    // signatories (Recorded By + Reviewed By) open via onSignatoriesClick.
  }, [])

  const openSignatoriesEditor = useCallback((weldId: string) => {
    setRecordedByEditWeldId(weldId)
    setSignaturesOpen(true)
  }, [])

  const resolvedRecordedByDrafts = useCallback(
    (sheet: FloorSheetPreviewData): SignatoryDraft[] => {
      const override = recordedByOverrides[sheet.weldId]
      if (override) return override
      return recordedByListFromPreview(sheet)
    },
    [recordedByOverrides]
  )

  const resolvedReviewedByDraft = useCallback(
    (sheet: FloorSheetPreviewData): SignatoryDraft => {
      const override = reviewedByOverrides[sheet.weldId]
      if (override) return override
      return recordedByDraftFromPreview(sheet.reviewedBy)
    },
    [reviewedByOverrides]
  )

  const isRecordedByDirty = useCallback(
    (sheet: FloorSheetPreviewData): boolean => {
      const override = recordedByOverrides[sheet.weldId]
      if (!override) return false
      const baseline = recordedByListFromPreview(sheet)
      return JSON.stringify(override) !== JSON.stringify(baseline)
    },
    [recordedByOverrides]
  )

  const isReviewedByDirty = useCallback(
    (sheet: FloorSheetPreviewData): boolean => {
      const override = reviewedByOverrides[sheet.weldId]
      if (!override) return false
      const baseline = recordedByDraftFromPreview(sheet.reviewedBy)
      return JSON.stringify(override) !== JSON.stringify(baseline)
    },
    [reviewedByOverrides]
  )

  const floorSheetSignatoriesDirty = useMemo(
    () =>
      preview.floorSheets.some(
        (sheet) => isRecordedByDirty(sheet) || isReviewedByDirty(sheet)
      ),
    [preview.floorSheets, isRecordedByDirty, isReviewedByDirty]
  )

  useUnsavedChangesGuard(
    () => staging.hasSignificantChanges() || floorSheetSignatoriesDirty
  )

  const signaturesModalWeldId = useMemo(
    () =>
      recordedByEditWeldId ??
      activeWeldId ??
      preview.floorSheets[0]?.weldId ??
      null,
    [recordedByEditWeldId, activeWeldId, preview.floorSheets]
  )

  const signaturesModalRecordedByInitial = useMemo((): SignatoryDraft[] => {
    if (!signaturesModalWeldId) return [{ id: '', name: '' }]
    const override = recordedByOverrides[signaturesModalWeldId]
    if (override) return override
    const sheet = preview.floorSheets.find((s) => s.weldId === signaturesModalWeldId)
    return sheet ? recordedByListFromPreview(sheet) : [{ id: '', name: '' }]
  }, [signaturesModalWeldId, recordedByOverrides, preview.floorSheets])

  const signaturesModalReviewedByInitial = useMemo((): SignatoryDraft => {
    if (!signaturesModalWeldId) return { id: '', name: '' }
    const override = reviewedByOverrides[signaturesModalWeldId]
    if (override) return override
    const sheet = preview.floorSheets.find((s) => s.weldId === signaturesModalWeldId)
    return sheet ? recordedByDraftFromPreview(sheet.reviewedBy) : { id: '', name: '' }
  }, [signaturesModalWeldId, reviewedByOverrides, preview.floorSheets])

  // ----- save flow ------------------------------------------------------
  const requiredFieldsMissing = useMemo(() => {
    const s = staging.stagedSnapshot
    const missing: string[] = []
    if (!s.pqrNumber.trim()) missing.push('PQR No.')
    if (!s.revision.trim()) missing.push('Rev.')
    for (const fs of preview.floorSheets) {
      const hasNamed = resolvedRecordedByDrafts(fs).some((d) => d.name.trim())
      if (!hasNamed) {
        missing.push(`Recorded By (${fs.weldId || 'Floor Sheet'})`)
        break
      }
    }
    return missing
  }, [staging.stagedSnapshot, preview.floorSheets, resolvedRecordedByDrafts])

  const saveAndGenerateDisabledReason =
    requiredFieldsMissing.length > 0
      ? `Fill required fields first: ${requiredFieldsMissing.join(', ')}`
      : undefined

  const performSave = useCallback(
    async (mode: PqrConfirmMode, options?: { notificationEmails?: string[] }) => {
      if (!id || !pipeId) return
      // Synchronous re-entrancy guard. The asynchronous `busy` state is
      // not reliable for back-to-back click handlers because React batches
      // state updates; using a ref keeps the second click a no-op.
      if (saveInFlightRef.current) return
      saveInFlightRef.current = true
      const s = staging.stagedSnapshot
      const baseline = staging.serverSnapshot
      setBusy(true)
      try {
        // Slice 08 of pqr-preview-field-navigation: Welding Parameters
        // overrides are no longer staged client-side. The destination
        // page persists edits directly to the per-weld telemetry, and
        // the server already reads from the same store on subsequent
        // PQR previews — nothing for the save flow to forward.
        void baseline
        // Defensive: `staging.sectionsVersion` is sourced from the `ETag`
        // header on `GET /sections`. If the header is hidden from JS (e.g.
        // a proxy or CORS misconfig strips it) we fall back to 0, which
        // would otherwise be sent verbatim as `ifMatchVersion: 0` and
        // collide with the server's real version on every save. Treat
        // `hasPersistedPqr=true && version=0` as "version unknown" and
        // send `null` — the server interprets that as "no concurrency
        // check" (matching first-save semantics) and the save proceeds.
        const ifMatchVersion =
          staging.hasPersistedPqr && staging.sectionsVersion > 0
            ? staging.sectionsVersion
            : null

        const recordedByByWeldId = Object.fromEntries(
          preview.floorSheets
            .map((sheet) => {
              const drafts = resolvedRecordedByDrafts(sheet).filter((d) => d.name.trim())
              if (drafts.length === 0) return null
              return [
                sheet.weldId,
                drafts.map(signatoryDraftToGeneratePerson)
              ] as const
            })
            .filter(
              (
                entry
              ): entry is [
                string,
                ReturnType<typeof signatoryDraftToGeneratePerson>[]
              ] => Boolean(entry)
            )
        )

        const reviewedByByWeldId = Object.fromEntries(
          preview.floorSheets
            .map((sheet) => {
              const draft = resolvedReviewedByDraft(sheet)
              if (!draft.name.trim()) return null
              return [sheet.weldId, signatoryDraftToGeneratePerson(draft)] as const
            })
            .filter(
              (
                entry
              ): entry is [string, ReturnType<typeof signatoryDraftToGeneratePerson>] =>
                Boolean(entry)
            )
        )

        const result = await generatePqrReport({
          projectId: id,
          pipeSetId: pipeId,
          body: {
            pqrNumber: s.pqrNumber,
            revision: s.revision,
            preparedBy: { id: '', name: '' },
            reviewedBy: { id: '', name: '' },
            // Comments is intentionally omitted (slice 03 of
            // pqr-preview-field-navigation): the canvas Comments cell
            // mirrors per-weld FloorSheet `operators.notes`, and the
            // server treats `comments` as optional and defaults to
            // empty when absent.
            ifMatchVersion,
            generatePdf: mode === 'saveAndGenerate',
            // SCOPED-PQR: when the user picked a weld on the entry-point
            // modal (or via "Change Weld"), thread it through so the
            // server scopes the same sections the canvas is showing.
            // Omitted = legacy aggregated PQR.
            weldId: activeWeldId,
            weldIds: activeWeldIds.length > 0 ? activeWeldIds : undefined,
            recordedByByWeldId:
              Object.keys(recordedByByWeldId).length > 0 ? recordedByByWeldId : undefined,
            reviewedByByWeldId:
              Object.keys(reviewedByByWeldId).length > 0 ? reviewedByByWeldId : undefined,
            notificationEmails:
              mode === 'saveAndGenerate' && options?.notificationEmails?.length
                ? options.notificationEmails
                : undefined
          }
        })

        // Per the no-prefill spec, we deliberately do NOT persist
        // signatories anywhere session-spanning. The sessionStorage
        // staged-snapshot cache (STAGED_SESSION_PREFIX) is wiped by the
        // sync effect below as soon as `markServerSaved` makes staged
        // equal to server (isDirty false).

        if (mode === 'saveAndGenerate' && result.report) {
          // Polish-pass slice 06: mount the in-app PdfViewer overlay
          // (with the freshly-presigned URL) so the user can review
          // and download the report inline. Replaces the old
          // window.open new-tab behaviour.
          setViewer({
            fileUrl: result.report.downloadUrl,
            fileName: result.report.name
          })
          toast.success('PQR saved. PDF ready.')
        } else {
          toast.success('PQR saved.')
        }

        staging.markServerSaved(s, result.sectionsVersion)
        setRecordedByOverrides({})
        setReviewedByOverrides({})
        setConfirmMode(null)
        setConfirmEmailOnly(false)
        setStaleBanner(null)

        // Re-aggregate the preview snapshot so the canvas reflects the
        // newly persisted state (welder lists, equipment lists, accompanying
        // reports and comments all flow through server-side aggregation).
        void preview.refetch()
        // Refresh the shared sections cache held by the surrounding
        // <WpsSectionsProvider>. Without this, when the user clicks a
        // canvas row and lands on an editor (still under the same pipe-
        // set layout), the editor's `updateSection(key, data)` would
        // PUT the entire `sections` blob with a stale `pqr` block —
        // silently rolling back the Rev / pqrNumber / signatories we
        // just saved. Refreshing pulls the freshly-saved `sections.pqr`
        // into the provider so the next editor PUT preserves it.
        if (wpsSections) {
          void wpsSections.refresh()
        }
      } catch (err: any) {
        if (err?.status === 409) {
          // Don't auto-reload — that would silently obliterate the user's
          // staged edits. Surface a clear banner + toast and let the user
          // decide whether to reload (losing edits) or retry.
          setStaleBanner(
            'Server has newer data than what your tab loaded. Your edits are still in place. Click "Reload server data" below to fetch the latest baseline — that will lose your staged edits — or close this banner and click Save again to retry.'
          )
          setConfirmMode(null)
          setConfirmEmailOnly(false)
          toast.error('Section version conflict — your save was not applied. See banner above.')
        } else {
          toast.error(err?.message || 'Failed to save PQR')
        }
      } finally {
        saveInFlightRef.current = false
        setBusy(false)
      }
    },
    [id, pipeId, staging, preview, activeWeldId, activeWeldIds, wpsSections, resolvedRecordedByDrafts, resolvedReviewedByDraft]
  )

  const requestSave = useCallback(
    (mode: PqrConfirmMode) => {
      const significant =
        staging.hasSignificantChanges() || floorSheetSignatoriesDirty
      if (mode === 'saveAndGenerate' && !significant) {
        setConfirmEmailOnly(true)
        setConfirmMode('saveAndGenerate')
        return
      }
      setConfirmEmailOnly(false)
      if (significant) {
        setConfirmMode(mode)
        return
      }
      if (!staging.isDirty() && !floorSheetSignatoriesDirty && mode === 'save') return
      void performSave(mode, { notificationEmails: [] })
    },
    [staging, performSave, floorSheetSignatoriesDirty]
  )

  const closeConfirmDialog = useCallback(() => {
    setConfirmMode(null)
    setConfirmEmailOnly(false)
  }, [])

  // ----- render guards --------------------------------------------------
  if (loading) {
    return <PageLoader label="Loading PQR preview…" />
  }

  if (!project || !pipeSet) {
    return (
      <Card title="PQR not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  if (loadError) {
    return (
      <Card title="Could not load PQR preview">
        <p className="text-sm text-error-700">{loadError}</p>
        <div className="mt-4">
          <Button onClick={() => void load()}>Retry</Button>
        </div>
      </Card>
    )
  }

  const hasSignificantChanges =
    staging.hasSignificantChanges() || floorSheetSignatoriesDirty
  const firstSave = !staging.hasPersistedPqr

  const isDirtyForDisplay = (section?: PqrSectionName) => {
    if (section === 'identifiers') {
      return false
    }
    if (section) {
      return staging.isDirty(section)
    }
    return hasSignificantChanges
  }

  const discardFloorSheetSignatories = () => {
    setRecordedByOverrides({})
    setReviewedByOverrides({})
  }

  const pqrPreviewSubtitleLine = joinMetaParts([
    formatPipeSetDimensions(pipeSet),
    hasSignificantChanges
      ? `${staging.stagedSignificantDiff.groups.reduce((n, g) => n + g.rows.length, 0)} unsaved change(s)`
      : null
  ])

  return (
    <div className="space-y-4">
      <PageHeader
        backTo={{
          to:
            id && pipeId
              ? `/projects/${encodeURIComponent(id)}/next/${encodeURIComponent(pipeId)}/pqr`
              : '/',
          label: 'Back to PQR'
        }}
        title={<ProjectPageTitle label="PQR Preview" projectName={project.name} />}
        subtitle={
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {pqrPreviewSubtitleLine ? <span>{pqrPreviewSubtitleLine}</span> : null}
            <PqrPreviewHeaderPills
              weldId={activeWeldId ?? undefined}
              weldNumber={activeWeldId ? weldNumberByWeldId[activeWeldId] : undefined}
              weldScopes={
                activeWeldIds.length > 1
                  ? activeWeldIds.map((wid) => ({
                      weldNumber: weldNumberByWeldId[wid]
                    }))
                  : undefined
              }
              pqrNumber={staging.stagedSnapshot.pqrNumber}
              revision={staging.stagedSnapshot.revision}
            />
          </div>
        }
        actions={
          <PqrPreviewToolbar
            isDirty={hasSignificantChanges}
            busy={busy}
            saveAndGenerateDisabledReason={saveAndGenerateDisabledReason}
            onDiscard={() => {
              if (!hasSignificantChanges) {
                staging.discardAll()
                discardFloorSheetSignatories()
                return
              }
              setPageConfirm({
                title: 'Discard staged changes?',
                description:
                  'All staged PQR edits will be reverted to the last saved values. This cannot be undone.',
                confirmLabel: 'Discard',
                tone: 'danger',
                onConfirm: () => {
                  staging.discardAll()
                  discardFloorSheetSignatories()
                  setPageConfirm(null)
                }
              })
            }}
            onSave={() => requestSave('save')}
            onSaveAndGenerate={() => requestSave('saveAndGenerate')}
            onExportExcel={preview.data ? handleExportExcel : undefined}
          />
        }
      />

      {staleBanner ? (
        <div
          className="flex flex-col gap-2 rounded-lg border border-hero-red-30 bg-hero-red-10 px-4 py-3 text-sm text-hero-red-90 sm:flex-row sm:items-center sm:justify-between"
          role="status"
          data-testid="pqr-stale-banner"
        >
          <span>{staleBanner}</span>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border border-hero-red-30 bg-white px-3 py-1 text-xs font-semibold text-hero-red-90 hover:bg-hero-red-20"
              onClick={() => {
                setPageConfirm({
                  title: 'Reload server baseline?',
                  description:
                    'The server has a newer version of this PQR. Reloading will discard your staged edits and pull the latest values.',
                  confirmLabel: 'Reload',
                  tone: 'danger',
                  onConfirm: () => {
                    setPageConfirm(null)
                    void load()
                  }
                })
              }}
            >
              Reload server data
            </button>
            <button
              type="button"
              className="rounded-md px-2 py-1 text-xs font-medium text-hero-red-90 underline"
              onClick={() => setStaleBanner(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {hasSignificantChanges ? (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-hero-red-30 bg-hero-red-10 px-4 py-2 text-sm text-hero-red-90"
          role="status"
          data-testid="pqr-unsaved-banner"
        >
          <span>
            You have unsaved staged changes. Click <strong>Save</strong> to persist them, or{' '}
            <strong>Save &amp; Generate Report</strong> to also produce a fresh PDF.
          </span>
          <button
            type="button"
            className="text-xs font-medium text-hero-red-90 underline"
            onClick={() => {
              setPageConfirm({
                title: 'Discard staged changes?',
                description:
                  'All staged PQR edits will be reverted to the last saved values. This cannot be undone.',
                confirmLabel: 'Discard',
                tone: 'danger',
                onConfirm: () => {
                  staging.discardAll()
                  setPageConfirm(null)
                }
              })
            }}
          >
            Discard
          </button>
        </div>
      ) : null}

      {preview.error ? (
        <div
          className="rounded-lg border border-error-300 bg-error-50 px-4 py-3 text-sm text-error-900"
          role="status"
        >
          Failed to load PQR preview: {preview.error}.{' '}
          <button
            type="button"
            className="text-xs font-medium text-error-900 underline"
            onClick={() => void preview.refetch()}
          >
            Retry
          </button>
        </div>
      ) : preview.data ? (
        <div className="space-y-6">
          <PqrPreviewCanvas
            data={composePreviewSnapshot(preview.data, staging.stagedSnapshot)}
            project={projectSummary}
            isDirty={isDirtyForDisplay}
            onSectionClick={handleSectionClick}
            fieldLinkContext={
              id && pipeId
                ? (() => {
                    const previewParams = new URLSearchParams()
                    if (activeWeldId) {
                      previewParams.set('weldId', activeWeldId)
                    }
                    if (activeWeldIds.length > 1) {
                      previewParams.set('weldIds', activeWeldIds.join(','))
                    }
                    const previewQs = previewParams.toString()
                    return {
                      projectId: id,
                      pipeSetId: pipeId,
                      weldId: activeWeldId ?? null,
                      weldIds: activeWeldIds.length > 0 ? activeWeldIds : undefined,
                      syntheticWeldId: activeWeldId
                        ? weldIdToSyntheticId[activeWeldId] ?? null
                        : null,
                      previewUrl: `/projects/${id}/next/${pipeId}/pqr/preview${
                        previewQs ? `?${previewQs}` : ''
                      }`
                    }
                  })()
                : undefined
            }
            fieldLinkActions={{
              isDirty: () => staging.hasSignificantChanges(),
              onDiscardAll: () => staging.discardAll(),
              onSaveAll: async () => {
                try {
                  await performSave('save')
                  return !staging.isDirty()
                } catch {
                  return false
                }
              }
            }}
          />

          {preview.floorSheets.length > 0 ? (
            preview.floorSheets.map((sheet, index) => (
              <FloorSheetPreviewCanvas
                key={`${sheet.weldId}-${index}`}
                data={{
                  ...mergeSignatoriesIntoSheet(
                    sheet,
                    recordedByOverrides[sheet.weldId],
                    reviewedByOverrides[sheet.weldId]
                  ),
                  pqrNumber:
                    staging.stagedSnapshot.pqrNumber.trim() ||
                    sheet.pqrNumber ||
                    ''
                }}
                projectName={projectSummary.projectName}
                isRecordedByDirty={isRecordedByDirty(sheet)}
                isReviewedByDirty={isReviewedByDirty(sheet)}
                onSignatoriesClick={() => openSignatoriesEditor(sheet.weldId)}
                fieldLinkContext={
                  id && pipeId
                    ? {
                        projectId: id,
                        pipeSetId: pipeId,
                        weldId: sheet.weldId || activeWeldId || null,
                        weldIds: activeWeldIds.length > 0 ? activeWeldIds : undefined,
                        syntheticWeldId: sheet.weldId
                          ? weldIdToSyntheticId[sheet.weldId] ?? null
                          : activeWeldId
                            ? weldIdToSyntheticId[activeWeldId] ?? null
                            : null,
                        previewUrl: `/projects/${id}/next/${pipeId}/pqr/preview${
                          (() => {
                            const previewParams = new URLSearchParams()
                            if (activeWeldId) previewParams.set('weldId', activeWeldId)
                            if (activeWeldIds.length > 1) {
                              previewParams.set('weldIds', activeWeldIds.join(','))
                            }
                            const qs = previewParams.toString()
                            return qs ? `?${qs}` : ''
                          })()
                        }`
                      }
                    : undefined
                }
                fieldLinkActions={{
                  isDirty: () => staging.hasSignificantChanges(),
                  onDiscardAll: () => staging.discardAll(),
                  onSaveAll: async () => {
                    try {
                      await performSave('save')
                      return !staging.isDirty()
                    } catch {
                      return false
                    }
                  }
                }}
              />
            ))
          ) : (
            <div
              className="rounded-lg border border-[#E2E5EA] bg-white px-4 py-8 text-center text-sm text-heritage-grey"
              data-testid="floorsheet-preview-empty"
            >
              No Floor Sheet data for this weld yet. Enter Floor Sheet fields, then
              refresh preview.
            </div>
          )}
        </div>
      ) : (
        <PageLoader label="Loading PQR preview…" />
      )}

      <IdentifiersModal
        open={identifiersOpen}
        initial={{
          pqrNumber: staging.stagedSnapshot.pqrNumber,
          revision: staging.stagedSnapshot.revision
        }}
        editInWpsHref={wpsFormRoot ? `${wpsFormRoot}/pqr` : undefined}
        onApply={(next) => {
          staging.apply({
            section: 'identifiers',
            pqrNumber: next.pqrNumber,
            revision: next.revision
          })
          setIdentifiersOpen(false)
        }}
        onCancel={() => setIdentifiersOpen(false)}
      />

      <SignaturesModal
        open={signaturesOpen}
        initial={{
          preparedBy: { id: '', name: '' },
          reviewedBy: signaturesModalReviewedByInitial,
          recordedByList: signaturesModalRecordedByInitial,
          recordedBy: signaturesModalRecordedByInitial[0]
        }}
        recordedByWeldId={signaturesModalWeldId}
        members={members}
        teamMembersHref={teamMembersHref}
        projectId={id}
        project={project}
        onMembersChanged={async () => {
          if (!id) return members
          const team = await fetchTeamMembers(id, { status: 'active', page: 1, pageSize: 200 })
          const next = team.items.filter((m) => m.status === 'active')
          setMembers(next)
          return next
        }}
        onApply={(next) => {
          staging.apply({
            section: 'signatures',
            preparedBy: { id: '', name: '' },
            reviewedBy: { id: '', name: '' }
          })
          const list =
            next.recordedByList && next.recordedByList.length > 0
              ? next.recordedByList
              : next.recordedBy
                ? [next.recordedBy]
                : null
          if (signaturesModalWeldId && list) {
            setRecordedByOverrides((prev) => ({
              ...prev,
              [signaturesModalWeldId]: list
            }))
          }
          if (signaturesModalWeldId) {
            setReviewedByOverrides((prev) => ({
              ...prev,
              [signaturesModalWeldId]: next.reviewedBy
            }))
          }
          setSignaturesOpen(false)
        }}
        onCancel={() => setSignaturesOpen(false)}
      />

      {/* MaterialsModal removed in Slice 05 of pqr-preview-field-navigation:
          per-pipe rows on the canvas now navigate to WpsBaseMaterial.
          JointPreparationModal was removed in round 3 of pqr-five-changes —
          the section is now read-only on the canvas.
          WeldingVariablesModal removed in Slice 07 of pqr-preview-field-
          navigation: per-cell clicks navigate to WpsWeldDetails.
          WeldingParametersModal removed in Slice 08 of pqr-preview-field-
          navigation: per-cell clicks navigate to the per-weld
          WeldParameters page. */}

      {/* pqr-report-history-and-rev slice 05: PqrWeldPickerModal was
          removed from this page. Weld scope is now fixed at picker time
          on PqrDetails (the user can only re-scope by starting a new
          draft via "Generate PQR Report") and surfaced here read-only
          via PqrPreviewHeaderPills. */}

      <PqrDiffConfirmDialog
        open={confirmMode !== null}
        busy={busy}
        diff={staging.stagedSignificantDiff}
        firstSave={firstSave}
        snapshot={staging.stagedSnapshot}
        mode={confirmMode ?? 'save'}
        emailOnly={confirmEmailOnly}
        onCancel={closeConfirmDialog}
        onConfirm={({ notificationEmails }) => {
          if (confirmMode) void performSave(confirmMode, { notificationEmails })
          closeConfirmDialog()
        }}
      />

      {/* Polish-pass slice 06: native window.confirm() replaced by the
          shared in-app ConfirmDialog. One mount serves the discard,
          reload-baseline, and unsaved-banner discard flows. */}
      <ConfirmDialog
        open={pageConfirm !== null}
        title={pageConfirm?.title ?? ''}
        description={pageConfirm?.description ?? ''}
        confirmLabel={pageConfirm?.confirmLabel ?? 'Confirm'}
        tone={pageConfirm?.tone}
        onConfirm={() => pageConfirm?.onConfirm()}
        onCancel={() => setPageConfirm(null)}
      />

      {/* Polish-pass slice 06: in-app PDF preview after Save & Generate
          so the user can review the freshly-generated report (and
          decide to download / open in a new tab) without leaving the
          PQR Preview page. */}
      {viewer && (
        <PdfViewer
          fileUrl={viewer.fileUrl}
          fileName={viewer.fileName}
          onClose={() => setViewer(null)}
        />
      )}

    </div>
  )
}

// Silence unused import warning when `navigate` is reserved for future routes.
void useNavigate
